/**
 * The provisioning ceremony signs with whatever account the browser wallet is
 * live on, while the app token it posts to belongs to the EOA that signed in.
 * When the two drift apart the backend can only answer
 * `ownerAuthorizationRequired`, so the drift is detected here, before a
 * signature is ever requested.
 */

import { getAddress, isAddressEqual, type Address } from 'viem';

/** The login's owner and the account the wallet has since moved to. */
export interface WalletMismatch {
    kind: 'walletMismatch';
    authenticatedOwner: Address;
    connectedAddress: Address;
}

/**
 * Null means "nothing stands in the way": no login owner is known (the
 * wallet-first and pre-login cohorts, where the connected wallet IS the
 * identity), no wallet is connected, or both name the same account.
 *
 * An unparseable address also yields null. It cannot be compared, and the
 * ceremony rejects it on its own terms a moment later with a better message
 * than a mismatch panel could give.
 */
export function detectWalletMismatch(
    authenticatedOwner: string | null | undefined,
    connectedAddress: string | null | undefined,
): WalletMismatch | null {
    if (!authenticatedOwner || !connectedAddress) return null;
    let owner: Address;
    let connected: Address;
    try {
        owner = getAddress(authenticatedOwner);
        connected = getAddress(connectedAddress);
    } catch {
        return null;
    }
    if (isAddressEqual(owner, connected)) return null;
    return {
        kind: 'walletMismatch',
        authenticatedOwner: owner,
        connectedAddress: connected,
    };
}
